// Separate file to handle route guards
import {
  isUserLoggedIn, getUserData, getUserRole, getHomeRouteForLoggedInUser,
} from '@/auth/utils'
import { canNavigate } from '@/libs/acl/routeProtection'

import Vue from 'vue'
import router from '@/router'
import settingsModule from '@/helpers/settings.module'
import blockingAnAccessSession from '@/views/components/blocking-an-access-session/blockingAnAccessSession'
import { computed } from '@vue/composition-api'
import settingsService from '@/services/settingsService'
import store from '../store'

function normalizePath(path) {
  if (!path) return '/'
  return path.replace(/\/+$/, '') || '/'
}

function resolveHomePath(homeRoute) {
  if (typeof homeRoute === 'string') {
    return normalizePath(homeRoute)
  }
  if (homeRoute && typeof homeRoute.path === 'string') {
    return normalizePath(homeRoute.path)
  }
  if (homeRoute && homeRoute.name) {
    return normalizePath(router.resolve(homeRoute).route.path)
  }
  return '/events'
}

export default function setupRouteGuards() {
  router.beforeEach((to, _, next) => {
    const isLoggedIn = isUserLoggedIn()
    const userData = getUserData()
    const userRole = getUserRole()
    const homeRoute = getHomeRouteForLoggedInUser(userData ? userRole : null)
    const homePath = resolveHomePath(homeRoute)

    if (isUserLoggedIn()) {
      store.state.isLoggedIn = true
      const { setSettingsToStore } = settingsModule()
      setSettingsToStore()
    } else {
      store.state.isLoggedIn = false
    }

    if (to.meta.redirectIfLoggedIn && isLoggedIn) {
      if (normalizePath(to.path) !== homePath) {
        return next(homeRoute)
      }
    }

    if (!canNavigate(to, isLoggedIn)) {
      // eslint-disable-next-line no-use-before-define
      return handleUnauthorizedAccess(to, isLoggedIn, next, userRole, homeRoute, homePath)
    }

    const numericParams = to.meta.numericParams || []
    if (numericParams.some(param => !/^\d+$/.test(to.params[param]))) {
      return next(homeRoute)
    }

    return next()
  })

  router.afterEach(to => {
    const appLoading = document.getElementById('loading-bg')
    if (appLoading) {
      appLoading.style.display = 'none'
    }

    Vue.nextTick(() => {
      document.title = `${to.meta.title || ''} - Ticket Place`
    })
  })
}

/**
 * Handles unauthorized access by redirecting users based on their login state and role.
 *
 * @param {Object} to - The target route object.
 * @param {Boolean} isLoggedIn - Whether the user is logged in or not.
 * @param {Function} next - The next function to resolve the navigation.
 * @param {String} userRole - The role of the user.
 * @param {String|Object} homeRoute - The home route for logged-in users based on their role.
 * @param {String} homePath - Normalized home path string.
 */
function handleUnauthorizedAccess(to, isLoggedIn, next, userRole, homeRoute, homePath) {
  if (!to.meta.notRedirectIfNotLoggedIn && !isLoggedIn) {
    if (to.name === 'auth-login') {
      return next()
    }
    return next({ name: 'auth-login' })
  }

  if (!to.meta.notRedirectIfLoggedIn && isLoggedIn) {
    if (userRole === 'client' && to.name !== 'client-orders') {
      const { logout } = blockingAnAccessSession()
      logout()
      const personalAccountUrl = computed(() => store.state.app.settings?.personal_account_url ?? settingsService.getSettings('common.personal_account_url'))
      document.location.replace(personalAccountUrl.value)
      return next(false)
    }
    if (to.name === 'misc-not-authorized') {
      return next()
    }
    if (normalizePath(to.path) !== homePath) {
      return next(homeRoute)
    }
    return next({ name: 'misc-not-authorized' })
  }

  return next()
}
